import React, { useState, useEffect } from 'react';
import axios from '../../utils/axiosConfig';
import { Users, Building2, Calendar, MapPin, TrendingUp, ShieldCheck, X, ExternalLink, Lock, Unlock, Trash2, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';

const SuperAdminDashboard = () => {
    const [metrics, setMetrics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeModal, setActiveModal] = useState(null); // 'salons' | 'events' | null

    useEffect(() => {
        const fetchMetrics = async () => {
            try {
                const response = await axios.get(`/api/superadmin/metrics`);
                setMetrics(response.data);
            } catch (err) {
                console.error('Error al cargar métricas:', err);
                setError('No se pudieron cargar las métricas. Asegúrate de tener permisos de SuperAdmin.');
            } finally {
                setLoading(false);
            }
        };

        fetchMetrics();
    }, []);

    const handleToggleBlock = async (salonId) => {
        try {
            const { data } = await axios.patch(`/api/superadmin/clients/${salonId}/toggle-status`);
            if (data.ok) {
                setMetrics(prev => ({
                    ...prev,
                    details: {
                        ...prev.details,
                        salons: prev.details.salons.map(s => s._id === salonId ? { ...s, isActive: data.isActive } : s)
                    }
                }));
            }
        } catch (err) {
            alert('Error al cambiar el estado: ' + (err.response?.data?.message || err.message));
        }
    };

    const handleDeleteSalon = async (salonId, salonName) => {
        if (!window.confirm(`¿Estás SEGURO de eliminar por completo la organización "${salonName}"? Esta acción no se puede deshacer.`)) return;
        
        try {
            const { data } = await axios.delete(`/api/superadmin/clients/${salonId}`);
            if (data.ok) {
                setMetrics(prev => {
                    const deletedOrganization = prev.details.salons.find(salon => salon._id === salonId);
                    const deletedType = deletedOrganization?.organizationType === 'events'
                        ? 'producer'
                        : deletedOrganization?.organizationType || 'producer';

                    return {
                        ...prev,
                        summary: {
                            ...prev.summary,
                            totalClients: prev.summary.totalClients - 1,
                            businessTypes: prev.summary.businessTypes?.map((businessType) => (
                                businessType.type === deletedType
                                    ? { ...businessType, count: Math.max(0, businessType.count - 1) }
                                    : businessType
                            ))
                        },
                        details: {
                            ...prev.details,
                            salons: prev.details.salons.filter(s => s._id !== salonId)
                        }
                    };
                });
            }
        } catch (err) {
            alert('Error al eliminar: ' + (err.response?.data?.message || err.message));
        }
    };

    if (loading) {
        return (
            <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4 bg-[#080808]">
                <div className="w-12 h-12 border-4 border-primary-orange/20 border-t-primary-orange rounded-full animate-spin"></div>
                <p className="text-white/20 font-bold uppercase tracking-widest text-xs">Cargando Métricas Globales...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-6 bg-[#080808]">
                <ShieldCheck size={48} className="text-red-500/50 mb-4" />
                <h2 className="text-2xl font-bold text-white mb-2">Acceso restringido</h2>
                <p className="text-white/40 max-w-sm">{error}</p>
            </div>
        );
    }

    const { summary, geoDistribution, details } = metrics;

    const Modal = ({ title, isOpen, onClose, children }) => {
        if (!isOpen) return null;
        return (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fade-in">
                <div className="bg-surface w-full max-w-2xl max-h-[80vh] rounded-[32px] border border-white/10 shadow-3xl overflow-hidden flex flex-col relative">
                    <div className="absolute top-0 left-0 w-full h-1 bg-brand-gradient"></div>
                    <div className="p-6 border-b border-white/5 flex items-center justify-between">
                        <h3 className="text-xl font-black text-white tracking-tight">{title}</h3>
                        <button onClick={onClose} className="p-2 rounded-full hover:bg-white/5 text-white/40 hover:text-white transition-colors">
                            <X size={20} />
                        </button>
                    </div>
                    <div className="p-6 overflow-y-auto custom-scrollbar">
                        {children}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="max-w-7xl mx-auto py-10 px-4 pb-32 animate-fade-in relative bg-[#080808] min-h-screen">
            {/* Header */}
            <div className="mb-12">
                <div className="flex items-center gap-3 mb-2">
                    <div className="p-1.5 bg-primary-orange/10 rounded-lg text-primary-orange">
                        <TrendingUp size={16} />
                    </div>
                    <span className="text-[9px] font-black uppercase tracking-[3px] text-white/30">Global Analytics</span>
                </div>
                <h1 className="text-3xl sm:text-4xl font-black text-white tracking-tight leading-none">
                    Panel de <span className="text-gradient">SuperAdmin</span>
                </h1>
            </div>

            {/* Tarjetas de Resumen */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                {/* Usuarios */}
                <div className="bg-surface p-8 rounded-[32px] border border-white/5 shadow-2xl relative overflow-hidden group transition-all">
                    <div className="absolute top-0 left-0 w-full h-1 bg-brand-gradient opacity-30"></div>
                    <div className="flex items-center gap-5">
                        <div className="p-4 bg-white/5 rounded-2xl text-primary-orange">
                            <Users size={28} />
                        </div>
                        <div>
                            <p className="text-[10px] text-white/30 font-bold uppercase tracking-widest mb-1">Total Usuarios</p>
                            <p className="text-3xl font-black text-white">{summary.totalUsers}</p>
                        </div>
                    </div>
                </div>

                {/* Organizaciones */}
                <div
                    onClick={() => setActiveModal('salons')}
                    className="bg-surface p-8 rounded-[32px] border border-white/5 shadow-2xl relative overflow-hidden group hover:border-purple-500/40 transition-all cursor-pointer active:scale-95"
                >
                    <div className="absolute top-0 left-0 w-full h-1 bg-purple-500 opacity-30 group-hover:opacity-100 transition-opacity"></div>
                    <div className="flex items-center gap-5">
                        <div className="p-4 bg-white/5 rounded-2xl text-purple-500">
                            <Building2 size={28} />
                        </div>
                        <div>
                            <p className="text-[10px] text-white/30 font-bold uppercase tracking-widest mb-1">Organizaciones registradas</p>
                            <p className="text-3xl font-black text-white">{summary.totalClients}</p>
                            <p className="text-[9px] text-purple-500 font-bold mt-1 opacity-0 group-hover:opacity-100 transition-opacity">Ver detalles →</p>
                        </div>
                    </div>
                </div>

                {/* Eventos */}
                <div
                    onClick={() => setActiveModal('events')}
                    className="bg-surface p-8 rounded-[32px] border border-white/5 shadow-2xl relative overflow-hidden group hover:border-green-500/40 transition-all cursor-pointer active:scale-95"
                >
                    <div className="absolute top-0 left-0 w-full h-1 bg-green-500 opacity-30 group-hover:opacity-100 transition-opacity"></div>
                    <div className="flex items-center gap-5">
                        <div className="p-4 bg-white/5 rounded-2xl text-green-500">
                            <Calendar size={28} />
                        </div>
                        <div>
                            <p className="text-[10px] text-white/30 font-bold uppercase tracking-widest mb-1">Eventos Creados</p>
                            <p className="text-3xl font-black text-white">{summary.totalEvents}</p>
                            <p className="text-[9px] text-green-500 font-bold mt-1 opacity-0 group-hover:opacity-100 transition-opacity">Ver vigentes →</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tipos de negocio */}
            <section className="mb-12">
                <div className="flex items-center justify-between gap-4 mb-5">
                    <div>
                        <h2 className="text-xl font-black text-white tracking-tight">Tipos de negocio</h2>
                        <p className="text-[10px] text-white/30 font-bold uppercase tracking-widest">Organizaciones registradas por categoría</p>
                    </div>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {(summary.businessTypes || []).map((businessType) => (
                        <div key={businessType.type} className="bg-surface p-5 rounded-2xl border border-white/5">
                            <p className="text-[9px] text-white/30 font-bold uppercase tracking-widest mb-2">{businessType.label}</p>
                            <p className="text-2xl font-black text-white">{businessType.count}</p>
                            <p className="text-[10px] text-white/40 font-medium mt-1">registrados</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Distribución Geográfica */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Usuarios por Provincia */}
                <section className="bg-surface p-8 md:p-10 rounded-[40px] border border-white/5 shadow-2xl relative overflow-hidden group">
                    <div className="absolute top-0 left-0 w-full h-1 bg-brand-gradient opacity-20 group-hover:opacity-100 transition-opacity"></div>
                    <div className="flex items-center gap-4 mb-10">
                        <div className="w-12 h-12 bg-white/5 rounded-2xl flex items-center justify-center text-primary-orange">
                            <MapPin size={24} />
                        </div>
                        <div>
                            <h2 className="text-2xl font-black text-white tracking-tight">Usuarios</h2>
                            <p className="text-[10px] text-white/30 font-bold uppercase tracking-widest">Distribución por Provincia</p>
                        </div>
                    </div>

                    <div className="space-y-6">
                        {geoDistribution.users.length > 0 ? (
                            geoDistribution.users.map((item) => (
                                <div key={item._id} className="space-y-2">
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="font-bold text-white/70">{item._id}</span>
                                        <span className="font-black text-primary-orange">{item.count}</span>
                                    </div>
                                    <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                                        <div
                                            className="h-full bg-brand-gradient rounded-full transition-all duration-1000 ease-out"
                                            style={{ width: `${(item.count / summary.totalUsers) * 100}%` }}
                                        />
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="py-10 text-center">
                                <p className="text-white/20 font-bold uppercase tracking-widest text-[10px] italic">Sin datos geográficos aún</p>
                            </div>
                        )}
                    </div>
                </section>

                {/* Organizaciones por Provincia */}
                <section className="bg-surface p-8 md:p-10 rounded-[40px] border border-white/5 shadow-2xl relative overflow-hidden group">
                    <div className="absolute top-0 left-0 w-full h-1 bg-purple-500 opacity-20 group-hover:opacity-100 transition-opacity"></div>
                    <div className="flex items-center gap-4 mb-10">
                        <div className="w-12 h-12 bg-white/5 rounded-2xl flex items-center justify-center text-purple-500">
                            <Building2 size={24} />
                        </div>
                        <div>
                            <h2 className="text-2xl font-black text-white tracking-tight">Organizaciones</h2>
                            <p className="text-[10px] text-white/30 font-bold uppercase tracking-widest">Distribución por Provincia</p>
                        </div>
                    </div>

                    <div className="space-y-6">
                        {geoDistribution.clients.length > 0 ? (
                            geoDistribution.clients.map((item) => (
                                <div key={item._id} className="space-y-2">
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="font-bold text-white/70">{item._id}</span>
                                        <span className="font-black text-purple-500">{item.count}</span>
                                    </div>
                                    <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                                        <div
                                            className="h-full bg-purple-500 rounded-full transition-all duration-1000 ease-out"
                                            style={{ width: `${(item.count / summary.totalClients) * 100}%` }}
                                        />
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="py-10 text-center">
                                <p className="text-white/20 font-bold uppercase tracking-widest text-[10px] italic">Sin datos geográficos aún</p>
                            </div>
                        )}
                    </div>
                </section>

                {/* Eventos por Provincia */}
                <section className="bg-surface p-8 md:p-10 rounded-[40px] border border-white/5 shadow-2xl relative overflow-hidden group">
                    <div className="absolute top-0 left-0 w-full h-1 bg-green-500 opacity-20 group-hover:opacity-100 transition-opacity"></div>
                    <div className="flex items-center gap-4 mb-10">
                        <div className="w-12 h-12 bg-white/5 rounded-2xl flex items-center justify-center text-green-500">
                            <Calendar size={24} />
                        </div>
                        <div>
                            <h2 className="text-2xl font-black text-white tracking-tight">Eventos</h2>
                            <p className="text-[10px] text-white/30 font-bold uppercase tracking-widest">Distribución por Provincia</p>
                        </div>
                    </div>

                    <div className="space-y-6">
                        {geoDistribution.events && geoDistribution.events.length > 0 ? (
                            geoDistribution.events.map((item) => (
                                <div key={item._id} className="space-y-2">
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="font-bold text-white/70">{item._id}</span>
                                        <span className="font-black text-green-500">{item.count}</span>
                                    </div>
                                    <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                                        <div
                                            className="h-full bg-green-500 rounded-full transition-all duration-1000 ease-out"
                                            style={{ width: `${(item.count / summary.totalEvents) * 100}%` }}
                                        />
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="py-10 text-center">
                                <p className="text-white/20 font-bold uppercase tracking-widest text-[10px] italic">Sin datos geográficos aún</p>
                            </div>
                        )}
                    </div>
                </section>
            </div>

            {/* MODALES DE DETALLE */}

            {/* Modal de organizaciones */}
            <Modal
                title="Listado de Organizaciones Registradas"
                isOpen={activeModal === 'salons'}
                onClose={() => setActiveModal(null)}
            >
                <div className="flex flex-col gap-3">
                    {details.salons.map((salon) => (
                        <div
                            key={salon._id}
                            className="p-4 bg-white/5 border border-white/5 rounded-2xl flex items-center justify-between group hover:border-white/10 transition-all"
                        >
                            <div className="flex items-center gap-4 flex-1">
                                <div className={`w-2 h-2 rounded-full ${salon.isActive === false ? 'bg-red-500 animate-pulse' : 'bg-green-500'}`} title={salon.isActive === false ? 'Bloqueado' : 'Activo'}></div>
                                <div>
                                    <div className="flex items-center gap-2 mb-1">
                                        <p className={`font-bold transition-colors text-lg ${salon.isActive === false ? 'text-white/40' : 'text-white'}`}>{salon.nombre}</p>
                                        <span className="px-2 py-1 bg-purple-500/10 text-purple-300 text-[9px] font-black uppercase rounded-md border border-purple-400/20">
                                            {salon.organizationLabel || salon.organizationShortLabel || 'Tipo sin definir'}
                                        </span>
                                        {salon.isActive === false && (
                                            <span className="px-1.5 py-0.5 bg-red-500/10 text-red-500 text-[8px] font-black uppercase rounded border border-red-500/20">Bloqueado</span>
                                        )}
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                        <div className="flex items-center gap-2 text-white/40 text-xs">
                                            <Mail size={12} />
                                            <span>{salon.adminEmail}</span>
                                        </div>
                                        <div className="flex items-center gap-4 text-[10px] font-bold text-white/30 uppercase tracking-widest">
                                            <span>SLUG: {salon.slug}</span>
                                            <span className="flex items-center gap-1"><Calendar size={10} className="text-purple-400" /> {salon.eventCount} EVENTOS</span>
                                            <span className="flex items-center gap-1"><Users size={10} className="text-orange-400" /> {salon.customerCount} CLIENTES</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="flex items-center gap-2">
                                <a
                                    href={`/${salon.slug}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="p-2.5 rounded-xl bg-white/5 text-white/40 hover:text-white hover:bg-white/10 transition-all"
                                    title="Visitar Espacio"
                                >
                                    <ExternalLink size={16} />
                                </a>

                                <button
                                    onClick={() => handleToggleBlock(salon._id)}
                                    className={`p-2.5 rounded-xl transition-all ${salon.isActive === false ? 'bg-green-500/10 text-green-500 hover:bg-green-500/20' : 'bg-red-500/10 text-red-500 hover:bg-red-500/20'}`}
                                    title={salon.isActive === false ? 'Desbloquear' : 'Bloquear Transacciones'}
                                >
                                    {salon.isActive === false ? <Unlock size={16} /> : <Lock size={16} />}
                                </button>

                                <button
                                    onClick={() => handleDeleteSalon(salon._id, salon.nombre)}
                                    className="p-2.5 rounded-xl bg-red-600/10 text-red-600 hover:bg-red-600 hover:text-white transition-all"
                                    title="Eliminar Salón"
                                >
                                    <Trash2 size={16} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </Modal>

            {/* Modal de Eventos */}
            <Modal
                title="Eventos Vigentes (Hoy en adelante)"
                isOpen={activeModal === 'events'}
                onClose={() => setActiveModal(null)}
            >
                <div className="space-y-4">
                    {details.activeEvents.length > 0 ? (
                        details.activeEvents.map((event) => (
                            <Link
                                key={event._id}
                                to={`/${event.clientId?.slug || ''}/events/${event._id}`}
                                className="p-5 bg-white/5 border border-white/5 rounded-2xl hover:bg-white/10 hover:border-green-500/30 transition-all group flex items-center justify-between"
                            >
                                <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-1">
                                        <p className="font-bold text-white group-hover:text-green-400 transition-colors">{event.title}</p>
                                        <span className="px-2 py-0.5 bg-green-500/10 text-green-500 text-[9px] font-black uppercase rounded-md">Vigente</span>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <p className="text-[10px] text-white/40 font-medium">
                                            📅 {new Date(event.date).toLocaleDateString()}
                                        </p>
                                        <p className="text-[10px] text-white/40 font-medium">
                                            🏢 {event.clientId?.nombre || 'Salón desconocido'}
                                        </p>
                                    </div>
                                </div>
                                <ExternalLink size={16} className="text-white/20 group-hover:text-green-400" />
                            </Link>
                        ))
                    ) : (
                        <p className="text-center py-10 text-white/20 font-bold uppercase tracking-widest text-xs">No hay eventos vigentes programados</p>
                    )}
                </div>
            </Modal>

        </div>
    );
};

export default SuperAdminDashboard;
